import React, {useCallback, useEffect, useRef, useState} from 'react';

export const INDIVIDUAL_CARDS_PAGE_SIZE = 8;
export const INDIVIDUAL_GRID_PAGE_SIZE = 20;
export const DEFAULT_INDIVIDUAL_PAGE_SIZE = INDIVIDUAL_CARDS_PAGE_SIZE;
export const INDIVIDUAL_PAGE_SIZE_BY_VISUALIZATION = {
    cards: INDIVIDUAL_CARDS_PAGE_SIZE,
    grid: INDIVIDUAL_GRID_PAGE_SIZE,
};
export const INDIVIDUAL_PAGE_SIZE_OPTIONS = [INDIVIDUAL_CARDS_PAGE_SIZE, INDIVIDUAL_GRID_PAGE_SIZE, 50, 100, 500];

export const getIndividualDefaultPageSize = (modeVisualization = "cards") => (
    INDIVIDUAL_PAGE_SIZE_BY_VISUALIZATION[modeVisualization] ?? DEFAULT_INDIVIDUAL_PAGE_SIZE
);

export const getIndividualPaginatedRowData = (rowData = [], currentPage = 1, pageSize = DEFAULT_INDIVIDUAL_PAGE_SIZE) => {
    const totalPages = Math.max(1, Math.ceil(rowData.length / pageSize));
    const activePage = Math.min(currentPage, totalPages);
    const start = (activePage - 1) * pageSize;

    return rowData.slice(start, start + pageSize);
}

const IndividualGridPagination = ({
    rowData = [],
    currentPage: controlledCurrentPage,
    pageSize: controlledPageSize,
    modeVisualization = "cards",
    onCurrentPageChange,
    onPageSizeChange,
}) => {
    const defaultPageSize = getIndividualDefaultPageSize(modeVisualization);
    const [internalCurrentPage, setInternalCurrentPage] = useState(1);
    const [internalPageSize, setInternalPageSize] = useState(defaultPageSize);
    const previousRowDataRef = useRef(rowData);

    const currentPage = controlledCurrentPage ?? internalCurrentPage;
    const pageSize = controlledPageSize ?? internalPageSize;
    const setCurrentPage = onCurrentPageChange ?? setInternalCurrentPage;
    const setPageSize = onPageSizeChange ?? setInternalPageSize;

    const totalItems = rowData.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    const activePage = Math.min(currentPage, totalPages);
    const currentPageItemsStart = totalItems === 0 ? 0 : (activePage - 1) * pageSize + 1;
    const currentPageItemsEnd = Math.min(activePage * pageSize, totalItems);

    useEffect(() => {
        if (currentPage > totalPages) setCurrentPage(totalPages);
    }, [currentPage, setCurrentPage, totalPages]);

    useEffect(() => {
        if (controlledPageSize !== null && controlledPageSize !== undefined) return;

        setInternalPageSize(defaultPageSize);
        setInternalCurrentPage(1);
    }, [controlledPageSize, defaultPageSize]);

    useEffect(() => {
        if (previousRowDataRef.current === rowData) return;

        previousRowDataRef.current = rowData;
        setCurrentPage(1);
    }, [rowData, setCurrentPage]);

    const handlePageSizeChange = useCallback((event) => {
        setPageSize(Number(event.target.value));
        setCurrentPage(1);
    }, [setCurrentPage, setPageSize]);

    return (
        <>
            <div className={"flex flex-col gap-3 py-1 text-sm text-gray-700 sm:flex-row sm:items-center"}>
                <span>
                    {currentPageItemsStart}-{currentPageItemsEnd} de {totalItems}
                </span>

                <div className={"flex flex-wrap items-center gap-2"}>
                    <label className={"flex items-center gap-2"}>
                        <span>Linhas por página</span>
                        <select
                            className={"rounded border border-gray-300 bg-white px-2 py-1"}
                            value={pageSize}
                            onChange={handlePageSizeChange}
                        >
                            {INDIVIDUAL_PAGE_SIZE_OPTIONS.map(option => (
                                <option key={option} value={option}>
                                    {option}
                                </option>
                            ))}
                        </select>
                    </label>

                    <button
                        className={"rounded border border-gray-300 px-3 py-1 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"}
                        type={"button"}
                        disabled={activePage === 1}
                        onClick={() => setCurrentPage(Math.max(activePage - 1, 1))}
                    >
                        Anterior
                    </button>

                    <span>
                        Página {activePage} de {totalPages}
                    </span>

                    <button
                        className={"rounded cursor-pointer border border-gray-300 px-3 py-1 disabled:cursor-not-allowed disabled:opacity-50"}
                        type={"button"}
                        disabled={activePage === totalPages}
                        onClick={() => setCurrentPage(Math.min(activePage + 1, totalPages))}
                    >
                        Próxima
                    </button>
                </div>
            </div>
        </>
    );
};

export default IndividualGridPagination;
